import { useState, useEffect } from 'react';
import ComponentCard from "../../common/ComponentCard";
import Label from '../../form/Label';
import Input from '../../form/input/InputField';
import Switch from '../../form/switch/Switch';
import Button from '../../ui/button/Button';
import Modal from '../../common/Modal';
import { AlertCircle } from 'lucide-react';
import api from '../../../services/api';
import authService from '../../../services/authService';
import Radio from '../../form/input/Radio';
import Select from '../../form/Select';

interface Integration {
    id: string;
    integrationUrl: string;
    accessToken?: string;
    apiKey?: string;
    status: 'active' | 'inactive';
    lastSyncAt?: string;
    createdAt?: string;
    active?: boolean;
    settings?: any;
    settingsData?: any;
}

interface InputErrors {
    token: string;
    number?: string;
}

type InvoiceType = 'nfse' | 'nfe';
type IssueTiming = 'after_warranty' | 'on_approved';

interface NotazzFormData {
    invoiceType: InvoiceType;
    issueTiming: IssueTiming;
    emailToCustomer: boolean;
    cancelOnRefund: boolean;
    includeInstallmentFee: boolean;
    address: {
        cep: string;
        street: string;
        number: string;
        complement: string;
        district: string; // bairro
        city: string;
        state: string; // UF
    };
}

export default function NotazzCard() {
    const [integration, setIntegration] = useState<Integration | null>(null);
    // Mantemos o nome accessToken internamente para minimizar mudanças, mas rotulamos como API Key
    const [accessToken, setAccessToken] = useState("");
    const [status, setStatus] = useState<'active' | 'inactive'>('active');
    const [loading, setLoading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'active' | 'inactive'>('inactive');
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        token: ''
    });
    const [openModal, setOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [formData, setFormData] = useState<NotazzFormData>({
        invoiceType: 'nfse',
        issueTiming: 'after_warranty',
        emailToCustomer: true,
        cancelOnRefund: true,
        includeInstallmentFee: true,
        address: {
            cep: '',
            street: '',
            number: '',
            complement: '',
            district: '',
            city: '',
            state: 'SP',
        },
    });
    const companyId = authService.getCompanyId();

    useEffect(() => {
        loadIntegration();
    }, []);

    async function loadIntegration() {
        try {
            const response = await api.get(`/api/integrations/${companyId}/notazz`);
            if (response.data.data) {
                const data = response.data.data;
                setIntegration(data);
                setAccessToken(data.apiKey || data.accessToken || '');
                // Usa o campo active para definir o status
                const connectionStatus = data.active ? 'active' : 'inactive';
                setConnectionStatus(connectionStatus);
                setStatus(connectionStatus);
                // Carrega configurações salvas (se existirem)
                const s = (data as any).settingsData || (data as any).settings;
                if (s) {
                    setFormData({
                        invoiceType: (s.invoiceType as InvoiceType) || 'nfse',
                        issueTiming: (s.issueTiming as IssueTiming) || 'after_warranty',
                        emailToCustomer: Boolean(s.emailToCustomer),
                        cancelOnRefund: Boolean(s.cancelOnRefund),
                        includeInstallmentFee: Boolean(s.includeInstallmentFee),
                        address: {
                            cep: s.address?.cep || '',
                            street: s.address?.street || '',
                            number: s.address?.number || '',
                            complement: s.address?.complement || '',
                            district: s.address?.district || '',
                            city: s.address?.city || '',
                            state: s.address?.state || 'SP',
                        },
                    });
                }
            } else {
                // Limpa os dados se não houver integração
                setIntegration(null);
                setAccessToken('');
                setConnectionStatus('inactive');
                setStatus('active');
                setFormData({
                    invoiceType: 'nfse',
                    issueTiming: 'after_warranty',
                    emailToCustomer: true,
                    cancelOnRefund: true,
                    includeInstallmentFee: true,
                    address: { cep: '', street: '', number: '', complement: '', district: '', city: '', state: 'SP' },
                });
            }
        } catch (error) {
            console.error('Erro ao carregar integração:', error);
            setApiError('Erro ao carregar dados da integração');
            setApiSuccess('');
            clearMessages();
        }
    }

    const handleStatusChange = (checked: boolean) => {
        setStatus(checked ? 'active' : 'inactive');
    };

    // Função para limpar as mensagens de erro e sucesso após alguns segundos
    const clearMessages = () => {
        setTimeout(() => {
            setApiError("");
            setApiSuccess("");
        }, 5000);
    };

    const handleRadioChange = (field: 'invoiceType' | 'issueTiming', value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value as any }));
    };

    // Busca CEP na BrasilAPI e preenche endereço
    const lookupCep = async (rawCep: string) => {
        const cep = (rawCep || '').replace(/\D/g, '');
        if (cep.length < 8) return; // só busca quando houver 8 dígitos
        try {
            const resp = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
            if (!resp.ok) throw new Error('CEP não encontrado');
            const data = await resp.json();
            setFormData((p) => ({
                ...p,
                address: {
                    ...p.address,
                    street: data?.street || p.address.street,
                    city: data?.city || p.address.city,
                    district: data?.neighborhood || p.address.district,
                    state: data?.state || p.address.state,
                },
            }));
        } catch (err) {
            console.error('Erro ao buscar CEP:', err);
            setApiError('CEP não encontrado ou serviço indisponível');
            setApiSuccess('');
            clearMessages();
        }
    };

    const validateFields = (): boolean => {
        let hasErrors = false;
        const newErrors: InputErrors = { token: '' };

        // Valida API Key
        if (!accessToken.trim()) {
            newErrors.token = 'API Key é obrigatória';
            hasErrors = true;
        }

        // Valida número do endereço
        if (!formData.address.number.trim()) {
            newErrors.number = 'Número é obrigatório';
            hasErrors = true;
        }

        setInputErrors(newErrors);
        return !hasErrors;
    };

    const handleSaveIntegration = async () => {
        if (!validateFields()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                provider: 'notazz',
                apiUrl: "https://app.notazz.com/api",
                apiKey: accessToken,
                status,
                settings: formData,
            };

            const response = await api.post(`/api/integrations/${companyId}`, payload);

            if (response.data.success) {
                setApiSuccess('Integração atualizada com sucesso!');
                setApiError('');
                clearMessages();
                await loadIntegration();
            } else {
                setApiError(response.data.error || 'Erro ao atualizar integração');
                setApiSuccess('');
                clearMessages();
            }
        } catch (error) {
            console.error('Erro ao salvar integração:', error);
            setApiError('Erro ao atualizar integração');
            setApiSuccess('');
            clearMessages();
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!integration) return;

        setIsLoading(true);
        try {
            const response = await api.delete(`/api/integrations/${companyId}/notazz`);

            if (response.data.success) {
                setApiSuccess('Integração desconectada com sucesso!');
                setApiError('');
                clearMessages();
                await loadIntegration();
                setOpenModal(false);
            } else {
                setApiError(response.data.error || 'Erro ao desconectar integração');
                setApiSuccess('');
                clearMessages();
            }
        } catch (error) {
            console.error('Erro ao desconectar integração:', error);
            setApiError('Erro ao desconectar integração');
            setApiSuccess('');
            clearMessages();
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusChip = (status: string, label: string) => {
        const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
        const statusClasses = status === 'active'
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";

        return (
            <span className={`${baseClasses} ${statusClasses}`}>
                {label}
            </span>
        );
    };

    const getInfoChip = (label: string, color: string = 'blue') => {
        const colorClasses = {
            blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
            purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[color as keyof typeof colorClasses] || colorClasses.blue}`}>
                {label}
            </span>
        );
    };

    const statesOptions = [
        { value: 'AC', label: 'AC' }, { value: 'AL', label: 'AL' }, { value: 'AP', label: 'AP' }, { value: 'AM', label: 'AM' },
        { value: 'BA', label: 'BA' }, { value: 'CE', label: 'CE' }, { value: 'DF', label: 'DF' }, { value: 'ES', label: 'ES' },
        { value: 'GO', label: 'GO' }, { value: 'MA', label: 'MA' }, { value: 'MT', label: 'MT' }, { value: 'MS', label: 'MS' },
        { value: 'MG', label: 'MG' }, { value: 'PA', label: 'PA' }, { value: 'PB', label: 'PB' }, { value: 'PR', label: 'PR' },
        { value: 'PE', label: 'PE' }, { value: 'PI', label: 'PI' }, { value: 'RJ', label: 'RJ' }, { value: 'RN', label: 'RN' },
        { value: 'RS', label: 'RS' }, { value: 'RO', label: 'RO' }, { value: 'RR', label: 'RR' }, { value: 'SC', label: 'SC' },
        { value: 'SP', label: 'SP' }, { value: 'SE', label: 'SE' }, { value: 'TO', label: 'TO' },
    ];

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="col-span-1 lg:col-span-2">
                    <ComponentCard title="Gerenciar integração Notazz">
                        {apiError && (
                            <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                {apiError}
                            </div>
                        )}

                        {apiSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                {apiSuccess}
                            </div>
                        )}

                        <div className="flex flex-col gap-4 mb-6">
                            <div className="flex items-center gap-4">
                                <img
                                    src="/images/integration/notazz-icon.png"
                                    alt="Notazz"
                                    className="w-10 h-10 object-contain"
                                />
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Notazz
                                    </h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Emita suas notas fiscais automaticamente integrando a Notazz.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200 dark:border-gray-700 mb-6" />

                        {/* Status da conexão */}
                        <div className="space-y-4 mb-6">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Status da conexão:</span>
                                {getStatusChip(
                                    connectionStatus,
                                    connectionStatus === 'active' ? 'Ativo' : 'Inativo'
                                )}
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Data da conexão:</span>
                                {getInfoChip(
                                    integration?.createdAt
                                        ? new Date(integration.createdAt).toLocaleDateString()
                                        : '--/--/--',
                                    'blue'
                                )}
                            </div>
                        </div>

                        <hr className="border-gray-200 dark:border-gray-700 mb-6" />

                        {/* Switch principal */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <Switch
                                label="Ativar integração"
                                checked={status === 'active'}
                                onChange={handleStatusChange}
                            />
                        </div>

                        {/* API Key */}
                        <div className="space-y-6 mb-6">
                            <div>
                                <Label htmlFor="accessToken">API Key:</Label>
                                <Input
                                    type="text"
                                    id="accessToken"
                                    placeholder="Informe sua API Key"
                                    value={accessToken}
                                    onChange={(e) => setAccessToken(e.target.value)}
                                    error={!!inputErrors.token}
                                    hint={inputErrors.token}
                                />
                            </div>
                        </div>

                        {/* Tipo de nota fiscal */}
                        <div className="mt-5">
                            <Label className="mb-2">Tipo de nota fiscal</Label>
                            <div className="flex flex-col gap-3 mt-2">
                                <Radio
                                    id="nfse"
                                    name="invoiceType"
                                    value="nfse"
                                    checked={formData.invoiceType === 'nfse'}
                                    onChange={(val) => handleRadioChange('invoiceType', val)}
                                    label="NFS-e (serviço) (recomendado)"
                                />
                                <Radio
                                    id="nfe"
                                    name="invoiceType"
                                    value="nfe"
                                    checked={formData.invoiceType === 'nfe'}
                                    onChange={(val) => handleRadioChange('invoiceType', val)}
                                    label="NF-e (produto)"
                                />
                            </div>
                        </div>

                        {/* Quando emitir */}
                        <div className="mt-6">
                            <Label className="mb-2">Quando emitir a nota fiscal?</Label>
                            <div className="flex flex-col gap-3 mt-2">
                                <Radio
                                    id="after_warranty"
                                    name="issueTiming"
                                    value="after_warranty"
                                    checked={formData.issueTiming === 'after_warranty'}
                                    onChange={(val) => handleRadioChange('issueTiming', val)}
                                    label="Emitir após o período de garantia"
                                />
                                <Radio
                                    id="on_approved"
                                    name="issueTiming"
                                    value="on_approved"
                                    checked={formData.issueTiming === 'on_approved'}
                                    onChange={(val) => handleRadioChange('issueTiming', val)}
                                    label="Emitir quando o pagamento for aprovado"
                                />
                            </div>
                        </div>

                        {/* Switches auxiliares */}
                        <div className="grid grid-cols-1 gap-4 mt-6">
                            <Switch
                                label="Enviar nota fiscal via email para o cliente?"
                                checked={formData.emailToCustomer}
                                onChange={(checked) => setFormData((p) => ({ ...p, emailToCustomer: checked }))}
                            />
                            <Switch
                                label="Cancelar nota fiscal quando houver um reembolso?"
                                checked={formData.cancelOnRefund}
                                onChange={(checked) => setFormData((p) => ({ ...p, cancelOnRefund: checked }))}
                            />
                            <Switch
                                label="Incluir acréscimo de parcelamento na nota fiscal?"
                                checked={formData.includeInstallmentFee}
                                onChange={(checked) => setFormData((p) => ({ ...p, includeInstallmentFee: checked }))}
                            />
                        </div>

                        {/* Endereço para a nota fiscal */}
                        <div className="mt-8">
                            <Label className="mb-3">Endereço para a nota fiscal</Label>
                            <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] p-4 border border-gray-200 dark:border-white/[0.05]">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="cep">CEP</Label>
                                        <Input
                                            id="cep"
                                            type="text"
                                            placeholder="CEP"
                                            value={formData.address.cep}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, cep: e.target.value } }))}
                                            onBlur={(e) => lookupCep(e.target.value)}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label htmlFor="street">Endereço</Label>
                                        <Input
                                            id="street"
                                            type="text"
                                            placeholder="Endereço"
                                            value={formData.address.street}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, street: e.target.value } }))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="number">Número*</Label>
                                        <Input
                                            id="number"
                                            type="text"
                                            placeholder="Número"
                                            value={formData.address.number}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, number: e.target.value } }))}
                                            error={!!inputErrors.number}
                                            hint={inputErrors.number}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="complement">Complemento</Label>
                                        <Input
                                            id="complement"
                                            type="text"
                                            placeholder="Complemento"
                                            value={formData.address.complement}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, complement: e.target.value } }))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="district">Bairro</Label>
                                        <Input
                                            id="district"
                                            type="text"
                                            placeholder="Bairro"
                                            value={formData.address.district}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, district: e.target.value } }))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="city">Cidade</Label>
                                        <Input
                                            id="city"
                                            type="text"
                                            placeholder="Cidade"
                                            value={formData.address.city}
                                            onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, city: e.target.value } }))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="state">Estado</Label>
                                        <Select
                                            options={statesOptions}
                                            placeholder="Estado"
                                            value={formData.address.state}
                                            onChange={(value) => setFormData((p) => ({ ...p, address: { ...p.address, state: value } }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200 dark:border-gray-700 mb-6" />

                        {/* Botões de ação */}
                        <div className="flex justify-between items-center gap-3">
                            <div>
                                {integration && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setOpenModal(true)}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Desconectando...' : 'Desconectar'}
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <Button size="sm" variant="outline" onClick={() => loadIntegration()} disabled={loading}>
                                    Cancelar
                                </Button>
                                <Button size="sm" variant="primary" onClick={handleSaveIntegration} disabled={loading}>
                                    {loading ? 'Salvando...' : 'Salvar alterações'}
                                </Button>
                            </div>
                        </div>
                    </ComponentCard>
                </div>
            </div>

            {/* Modal de confirmação */}
            <Modal
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
                size="sm"
                onConfirm={handleDisconnect}
                confirmText="Confirmar"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={isLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Deseja desconectar a integração com a Notazz?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                        Esta ação irá remover a integração e não será possível reverter.
                    </p>
                </div>
            </Modal>
        </>
    );
}